import type {
  AiBlock,
  AiChatResponse,
  AiConversationMessage,
  AiSource,
} from "../hooks/useAiChat";

export type AiChatFixtureScenario =
  "reasoning" | "answer" | "conversation" | "commerce" | "missing_data";

export type AiChatFixture = {
  scenario: AiChatFixtureScenario;
  messages: AiConversationMessage[];
  isLoading: boolean;
  response: AiChatResponse;
};

const sources: AiSource[] = [
  {
    id: "fixture-asrm-2024",
    label: "ASRM committee opinion",
    source_type: "guideline",
    organization: "ASRM",
    year: 2024,
    is_clickable: false,
    quality_hint: "Guideline",
  },
  {
    id: "fixture-eshre-2023",
    label: "ESHRE fertility preservation guidance",
    source_type: "guideline",
    organization: "ESHRE",
    year: 2023,
    is_clickable: false,
    quality_hint: "Guideline",
  },
];

const reasoningBlock: AiBlock = {
  type: "reasoning",
  title: "Reasoning",
  summary:
    "I’m checking your AMH result against your recorded health history and goals so I can explain what it means in context.",
  categories: [
    {
      key: "profile",
      label: "Your profile",
      items: [
        {
          id: "age",
          label: "Age",
          value: "34",
          status: "present",
        },
        {
          id: "goal",
          label: "Goal",
          value: "Egg freezing",
          status: "present",
        },
      ],
    },
    {
      key: "test_results",
      label: "Your test results",
      items: [
        {
          id: "amh",
          label: "AMH",
          value: "0.9 ng/mL · Jun 1",
          status: "present",
        },
        {
          id: "vitamin_d",
          label: "Vitamin D",
          value: "Not on file",
          status: "missing",
        },
      ],
    },
    {
      key: "treatment_context",
      label: "Treatment context",
      items: [
        {
          id: "clinic",
          label: "Clinic plan",
          value: "Not recorded yet",
          status: "uncertain",
        },
      ],
    },
  ],
  sources,
};

const answerBlock: AiBlock = {
  type: "simple_answer",
  title: "The short version",
  content:
    "Your AMH result is one piece of the picture. It can help your clinician estimate how your ovaries may respond to stimulation, but it does not predict whether you can become pregnant. Its meaning is clearer alongside your antral follicle count, age, and fertility goals.",
  sources,
};

const commerceAnswerBlock: AiBlock = {
  type: "simple_answer",
  title: "Before you buy another test",
  content:
    "Before buying another test, check what you want the result to change and whether your clinic would use it. An at-home hormone test can add a second data point, but it may overlap with testing you already have and it does not replace clinic testing or an ultrasound. If you share the tests you have already done, I can help compare what another one would add.",
  sources,
};

const actionPlanBlock: AiBlock = {
  type: "action_plan",
  title: "What to discuss next",
  summary:
    "These are conversation starters for your clinician, not prescriptions or checkout recommendations.",
  items: [
    {
      id: "product-modern-fertility",
      kind: "test",
      label: "At-home fertility hormone test",
      status: "active",
      priority: "medium",
      recommendation_text:
        "Use only if your clinician says an additional home result would add useful context.",
      rationale:
        "A home result should complement, not replace, the clinic's AMH and ultrasound measurements.",
      quality_of_evidence: "insufficient",
      related_datapoints: ["amh"],
      related_sources: ["fixture-asrm-2024"],
      follow_ups: [
        "What does this test include?",
        "Would my clinic accept these results?",
      ],
      card: {
        title: "At-home fertility hormone test",
        subtitle: "AMH, FSH, LH and estradiol",
        body: "A design example of a purchasable test row with price, discount, and details.",
      },
    },
    {
      id: "supplement-coq10",
      kind: "supplement",
      label: "CoQ10 / Ubiquinol",
      status: "active",
      priority: "low",
      recommendation_text:
        "Ask whether this supplement is appropriate for your plan before starting it.",
      rationale:
        "The right supplement depends on your medications, treatment timing, and clinician's advice.",
      quality_of_evidence: "moderate",
      related_datapoints: ["goal"],
      related_sources: ["fixture-eshre-2023"],
      follow_ups: ["What should I check first?"],
      card: {
        title: "CoQ10 / Ubiquinol",
        subtitle: "Supports egg cell energy",
        body: "The detail affordance opens the existing supplement content sheet.",
      },
    },
  ],
};

const missingDataBlock: AiBlock = {
  type: "missing_data_questions",
  title: "Something is missing from your record",
  summary:
    "I checked the results related to your question, but I don’t see an AMH result saved yet. That’s different from an error.",
  questions: [
    {
      id: "missing-amh-result",
      question: "Would you like to share an AMH result?",
      why_it_matters:
        "A result with its date and unit gives Louise something specific to explain.",
      target_field: "amh_result",
    },
    {
      id: "missing-amh-test",
      question: "Would you like to explore AMH testing options?",
      why_it_matters:
        "You can compare what a test measures with what your clinic already has.",
      target_field: "amh_test_options",
    },
  ],
};

const answerResponse: AiChatResponse = {
  message_type: "deep_dive",
  blocks: [reasoningBlock, answerBlock],
};

const commerceResponse: AiChatResponse = {
  message_type: "deep_dive",
  blocks: [commerceAnswerBlock, actionPlanBlock],
};

const missingDataResponse: AiChatResponse = {
  message_type: "simple_answer",
  blocks: [
    {
      type: "reasoning",
      title: "What I checked",
      summary:
        "I checked the results related to your question and looked for an AMH result in your record.",
      categories: [
        {
          key: "test_results",
          label: "Your test results",
          items: [
            {
              id: "amh",
              label: "AMH",
              value: "Not on file",
              status: "missing",
            },
          ],
        },
      ],
      sources: [],
    },
    {
      type: "simple_answer",
      title: "What I found",
      content:
        "It doesn’t seem like you have an AMH result saved in Louise yet. If you have a result, you can share it with me and I can help explain what it says.",
      sources: [],
    },
    missingDataBlock,
  ],
};

const reasoningOnlyMessages: AiConversationMessage[] = [
  {
    role: "user",
    text: "What does my AMH result mean?",
  },
  {
    role: "assistant",
    thinking: true,
    response: {
      message_type: "deep_dive",
      blocks: [reasoningBlock],
    },
  },
];

const answerMessages: AiConversationMessage[] = [
  {
    role: "user",
    text: "What does my AMH result mean?",
  },
  {
    role: "assistant",
    thinking: false,
    response: answerResponse,
  },
];

const commerceMessages: AiConversationMessage[] = [
  {
    role: "user",
    text: "What should I consider before I buy another test?",
  },
  {
    role: "assistant",
    thinking: false,
    response: commerceResponse,
  },
];

const conversationMessages: AiConversationMessage[] = [
  ...answerMessages,
  {
    role: "user",
    text: "What should I ask about before I buy another test?",
  },
  {
    role: "assistant",
    thinking: false,
    response: commerceResponse,
  },
];

const missingDataMessages: AiConversationMessage[] = [
  {
    role: "user",
    text: "Do I have an AMH result in my record?",
  },
  {
    role: "assistant",
    thinking: false,
    response: missingDataResponse,
  },
];

function isFixtureEnabled(): boolean {
  return __DEV__ && typeof window !== "undefined";
}

export function getAiChatFixture(): AiChatFixture | null {
  if (!isFixtureEnabled()) return null;

  const value = new URLSearchParams(window.location.search).get(
    "dev_ai_chat_fixture",
  );
  if (
    value !== "reasoning" &&
    value !== "answer" &&
    value !== "conversation" &&
    value !== "commerce" &&
    value !== "missing_data"
  ) {
    return null;
  }

  const messages =
    value === "reasoning"
      ? reasoningOnlyMessages
      : value === "conversation"
        ? conversationMessages
        : value === "missing_data"
          ? missingDataMessages
          : value === "commerce"
            ? commerceMessages
            : answerMessages;

  return {
    scenario: value,
    messages,
    isLoading: value === "reasoning",
    response:
      value === "commerce"
        ? commerceResponse
        : value === "missing_data"
          ? missingDataResponse
          : answerResponse,
  };
}
